import { CartLinesUpdateEvent } from '@shopify/events';

const RESET_MS = 1800;

async function readCart() {
  const items = document.querySelector('cart-items-component');
  if (items) {
    await customElements.whenDefined('cart-items-component');
    if (typeof items.fetchCartData === 'function') return items.fetchCartData();
  }
  const res = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  return res.json();
}

// Quick add
document.addEventListener('submit', async (event) => {
  const form = event.target instanceof HTMLFormElement ? event.target : null;
  if (!form || !form.hasAttribute('data-amb-add')) return;
  event.preventDefault();

  const button = form.querySelector('button[type="submit"]');
  if (!button || button.disabled) return;
  button.disabled = true;
  button.dataset.state = 'adding';

  const data = new FormData(form);
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) data.append('sections', sectionIds.join(','));

  const quantity = Number(data.get('quantity')) || 1;
  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(data.get('id')), quantity }],
      promise: deferred.promise,
    })
  );

  let failed = false;
  try {
    const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      body: data,
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    const json = await res.json();
    failed = Boolean(json.status);
    const cart = await readCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'amb-quick-add',
        sourceId: form.id || 'amb-quick-add',
        itemCount: quantity,
        sections: json.sections,
        didError: failed,
      },
    });
  } catch (error) {
    failed = true;
    deferred.reject(error);
  }

  button.dataset.state = failed ? 'error' : 'added';
  const live = form.querySelector('[data-amb-live]');
  if (live) live.textContent = failed ? 'Unable to add to cart. Please try again.' : 'Added to cart';

  setTimeout(() => {
    button.disabled = false;
    delete button.dataset.state;
    if (live) live.textContent = '';
  }, RESET_MS);
});

// Tabs
function initTabs(root) {
  const tabs = [...root.querySelectorAll('[role="tab"][data-amb-tab]')];
  const panels = [...root.querySelectorAll('[data-amb-panel]')];
  if (!tabs.length || !panels.length) return;

  function select(name, moveFocus) {
    tabs.forEach((tab) => {
      const on = tab.dataset.ambTab === name;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
      if (on && moveFocus) tab.focus();
    });
    panels.forEach((panel) => {
      panel.hidden = panel.dataset.ambPanel !== name;
    });
  }

  root.querySelectorAll('[data-amb-tab]').forEach((el) => {
    el.addEventListener('click', () => {
      select(el.dataset.ambTab, el.getAttribute('role') !== 'tab');
    });
  });

  tabs.forEach((tab, i) => {
    tab.addEventListener('keydown', (event) => {
      let next = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = tabs[(i + 1) % tabs.length];
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = tabs[(i - 1 + tabs.length) % tabs.length];
      else if (event.key === 'Home') next = tabs[0];
      else if (event.key === 'End') next = tabs[tabs.length - 1];
      if (!next) return;
      event.preventDefault();
      select(next.dataset.ambTab, true);
    });
  });

  // Deep link to a tab via data-amb-hash
  function openFromHash(hash) {
    const tab = tabs.find((t) => t.dataset.ambHash === hash);
    if (!tab) return false;
    select(tab.dataset.ambTab, false);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    root.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
    return true;
  }

  window.addEventListener('hashchange', () => openFromHash(decodeURIComponent(location.hash.slice(1))));

  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href*="#"]') : null;
    if (!link) return;
    const hash = decodeURIComponent((link.hash || '').slice(1));
    if (!hash || !tabs.some((t) => t.dataset.ambHash === hash)) return;
    if (link.pathname !== location.pathname || link.host !== location.host) return;
    event.preventDefault();
    if (location.hash !== `#${hash}`) history.replaceState(null, '', `#${hash}`);
    openFromHash(hash);
  });

  const initial = decodeURIComponent(location.hash.slice(1));
  if (initial) openFromHash(initial);
}

// Build your own set
async function addLines(lines, origin, sourceId) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

  const deferred = CartLinesUpdateEvent.createPromise();
  origin.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: lines.map((line) => ({ id: Number(line.id), quantity: line.quantity })),
        ...(sectionIds.length ? { sections: sectionIds.join(',') } : {}),
      }),
    });
    const json = await res.json();
    const failed = Boolean(json.status);
    const cart = await readCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'amb-bundle',
        sourceId,
        itemCount: lines.reduce((sum, line) => sum + line.quantity, 0),
        sections: json.sections,
        didError: failed,
      },
    });
    return !failed;
  } catch (error) {
    deferred.reject(error);
    return false;
  }
}

function initBundle(root) {
  const picks = [...root.querySelectorAll('[data-amb-pick]')];
  if (!picks.length) return;

  const countEl = root.querySelector('[data-amb-count]');
  const totalEl = root.querySelector('[data-amb-total]');
  const listEl = root.querySelector('[data-amb-list]');
  const barEl = root.querySelector('[data-amb-bar]');
  const shipEl = root.querySelector('[data-amb-ship]');
  const rescueEl = root.querySelector('[data-amb-rescue]');
  const addBtn = root.querySelector('[data-amb-bundle-add]');
  const labelEl = root.querySelector('[data-amb-label]');
  const swapEl = root.querySelector('[data-amb-swap]');
  const swapBtn = root.querySelector('[data-amb-swap-btn]');
  const liveEl = root.querySelector('[data-amb-bundle-live]');

  const threshold = Number(root.dataset.threshold) || 0;
  const emptyShip = shipEl ? shipEl.textContent : '';
  const idleLabel = labelEl ? labelEl.textContent.trim() : 'Add to Cart';
  const setMembers = picks.filter((p) => p.dataset.inSet === 'true');

  const money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency: root.dataset.currency || 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  const fmt = (cents) => money.format(cents / 100);
  const chosen = () => picks.filter((p) => p.getAttribute('aria-pressed') === 'true');

  function render() {
    const on = chosen();
    const total = on.reduce((sum, p) => sum + Number(p.dataset.price || 0), 0);

    if (countEl) {
      countEl.textContent = on.length
        ? `${on.length} ${on.length === 1 ? 'bag' : 'bags'} selected`
        : 'No bags selected';
    }
    if (totalEl) totalEl.textContent = on.length ? fmt(total) : fmt(0);

    if (listEl) {
      listEl.replaceChildren(
        ...on.map((p) => {
          const li = document.createElement('li');
          const name = document.createElement('span');
          name.textContent = p.dataset.title || '';
          const price = document.createElement('span');
          price.textContent = fmt(Number(p.dataset.price || 0));
          li.append(name, price);
          return li;
        })
      );
    }

    if (barEl) {
      const pct = threshold > 0 ? Math.min(100, Math.round((total / threshold) * 100)) : 0;
      barEl.style.width = `${pct}%`;
    }
    if (shipEl) {
      if (!on.length) shipEl.textContent = emptyShip;
      else if (threshold > 0 && total >= threshold) shipEl.textContent = 'You qualify for free shipping.';
      else if (threshold > 0) shipEl.textContent = `You're ${fmt(threshold - total)} away from free shipping.`;
      else shipEl.textContent = emptyShip;
    }
    if (rescueEl) rescueEl.hidden = on.length === 0;

    if (addBtn) addBtn.disabled = on.length === 0;
    if (labelEl) labelEl.textContent = on.length ? `Add to Cart · ${fmt(total)}` : idleLabel;

    if (swapEl && setMembers.length) {
      const exactSet = on.length === setMembers.length && setMembers.every((p) => on.includes(p));
      const setPrice = swapBtn ? Number(swapBtn.dataset.price || 0) : 0;
      swapEl.hidden = !(exactSet && setPrice > 0 && setPrice < total);
    }
  }

  picks.forEach((pick) => {
    pick.addEventListener('click', () => {
      if (pick.disabled) return;
      pick.setAttribute('aria-pressed', pick.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
      render();
    });
  });

  if (addBtn) {
    addBtn.addEventListener('click', async () => {
      const on = chosen();
      if (!on.length || addBtn.disabled) return;
      addBtn.disabled = true;
      addBtn.dataset.state = 'adding';

      const ok = await addLines(
        on.map((p) => ({ id: p.dataset.variant, quantity: 1 })),
        addBtn,
        root.id || 'amb-bundle'
      );

      addBtn.dataset.state = ok ? 'added' : 'error';
      if (liveEl) liveEl.textContent = ok ? 'Added to cart' : 'Unable to add to cart. Please try again.';

      setTimeout(() => {
        delete addBtn.dataset.state;
        if (liveEl) liveEl.textContent = '';
        if (ok) picks.forEach((p) => p.setAttribute('aria-pressed', 'false'));
        render();
      }, RESET_MS);
    });
  }

  if (swapBtn) {
    swapBtn.addEventListener('click', async () => {
      if (swapBtn.disabled) return;
      swapBtn.disabled = true;

      const ok = await addLines([{ id: swapBtn.dataset.variant, quantity: 1 }], swapBtn, root.id || 'amb-bundle-set');

      if (liveEl) liveEl.textContent = ok ? 'Complete Set added to cart' : 'Unable to add to cart. Please try again.';

      setTimeout(() => {
        swapBtn.disabled = false;
        if (liveEl) liveEl.textContent = '';
        if (ok) picks.forEach((p) => p.setAttribute('aria-pressed', 'false'));
        render();
      }, RESET_MS);
    });
  }

  render();
}

function bootBundles() {
  document.querySelectorAll('[data-amb-bundle]').forEach((root) => {
    if (root.dataset.ambBooted === 'true') return;
    root.dataset.ambBooted = 'true';
    initTabs(root);
    initBundle(root);
  });
}

bootBundles();
document.addEventListener('shopify:section:load', bootBundles);

// Video posters
document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-amb-play]') : null;
  if (!button) return;
  const template = button.nextElementSibling;
  if (!(template instanceof HTMLTemplateElement)) return;
  const holder = button.parentElement;
  button.replaceWith(template.content.cloneNode(true));
  template.remove();
  const video = holder?.querySelector('video');
  if (video) {
    video.focus();
    video.play().catch(() => {});
  }
});
